import { createEntityAdapter } from '@ngrx/entity';
import { EDocument, EDocumentsCategory, LoadingStatus } from '@qvs/core';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import * as fromActions from './e-documents.actions';
import { onEntityLoadingStatus, onNestedLoadingStatus } from '~/shared/helpers/loading-status-helper';

const eDocumentsEntityAdapter = createEntityAdapter<EDocument>();

export const initialState = eDocumentsEntityAdapter.getInitialState({
  categories: {
    value: undefined as EDocumentsCategory[],
    loadingStatus: LoadingStatus.IDLE
  },
  loadingStatus: LoadingStatus.IDLE,
  currentBeginDate: null,
  isAllDocumentsLoadComplete: false,
  firstBatchLoadingStatus: LoadingStatus.IDLE
});

export type State = typeof initialState;

export const eDocumentsFeatureKey = 'eDocuments';

const reducer = createReducer(
  initialState,
  ...onEntityLoadingStatus<State>(
    fromActions.loadEDocumentsInBatches,
    fromActions.loadEDocumentsInBatchesSuccess,
    fromActions.loadEDocumentsInBatchesFailure
  ),
  on(fromActions.upsertEDocuments, (state, { entities }) => eDocumentsEntityAdapter.upsertMany(entities, state)),
  ...onNestedLoadingStatus<State>(
    fromActions.loadEDocumentsCategories,
    fromActions.loadEDocumentsCategoriesFailure,
    'categories'
  ),
  on(fromActions.loadEDocumentsCategoriesSuccess, (state, { response }) => ({
    ...state,
    categories: {
      value: response.eDocumentsCategories,
      loadingStatus: LoadingStatus.SUCCEEDED
    }
  })),
  on(fromActions.resetEDocumentsState, (state) => ({ ...initialState, categories: state.categories })),
  on(fromActions.setEDocumentsCurrentBeginDate, (state, { currentBeginDate }) => ({
    ...state,
    currentBeginDate: currentBeginDate
  })),
  on(fromActions.loadAllEDocumentsSuccess, (state) => ({
    ...state,
    isAllDocumentsLoadComplete: true
  })),
  on(fromActions.loadEDocumentsInBatches, (state, { payload }) => ({
    ...state,
    firstBatchLoadingStatus: payload?.isFirstBatch ? LoadingStatus.LOADING : state.firstBatchLoadingStatus
  })),
  on(fromActions.loadEDocumentsFirstBatchSuccess, (state) => ({
    ...state,
    firstBatchLoadingStatus: LoadingStatus.SUCCEEDED
  })),
  on(fromActions.loadEDocumentsFirstBatchFailure, (state) => ({
    ...state,
    firstBatchLoadingStatus: LoadingStatus.FAILED
  })),
  on(fromActions.loadEDocumentsMediaAsJsonSuccess, (state, { documentId, eDocumentsMedia }) =>
    eDocumentsEntityAdapter.updateOne({ id: documentId, changes: { media: eDocumentsMedia } }, state)
  )
);

export const eDocumentsFeature = createFeature({
  name: eDocumentsFeatureKey,
  reducer
});

const { selectEDocumentsState } = eDocumentsFeature;
const { selectEntities, selectAll } = eDocumentsEntityAdapter.getSelectors(selectEDocumentsState);

export const selectAllEDocuments = selectAll;

export const selectEDocumentsCategories = createSelector(selectEDocumentsState, (state) => state.categories.value);

export const selectEDocumentById = (documentId: string) => createSelector(selectEntities, (entities) => entities[documentId]);

export const selectEDocumentsByPortfolioId = (portfolioNumber: string) =>
  createSelector(selectAllEDocuments, (eDocuments) =>
    eDocuments.filter((eDocument) => portfolioNumber.includes(eDocument.portfolioId))
  );

export const selectEDocumentsIsLoading = createSelector(
  selectEDocumentsState,
  (state) => state.loadingStatus === LoadingStatus.LOADING
);

export const selectEDocumentsCurrentBeginDate = createSelector(selectEDocumentsState, (state) => state.currentBeginDate);

export const selectEDocumentsIsFirstBatchLoading = createSelector(
  selectEDocumentsState,
  (state) => state.firstBatchLoadingStatus === LoadingStatus.LOADING
);

export const selectEDocumentsIsAllDocumentsLoadComplete = createSelector(
  selectEDocumentsState,
  (state) => state.isAllDocumentsLoadComplete
);
